// Maps a risk/return metric value to a coarse good / ok / bad quality, so the UI can
// color-code figures at a glance. The thresholds are deliberate rules of thumb for a
// broad-market, long-horizon investor, not precise cutoffs. They give a quick read, not
// investment advice. A metric with no clear "better" direction (beta, correlation) has no
// quality and is shown neutrally.
//
// Convention: "good" is favorable (green), "ok" is middling (amber), "bad" is unfavorable
// (red). Percent inputs are percents (e.g. 14.2 means 14.2%), matching the API payload.

export type Quality = "good" | "ok" | "bad";

// Metrics we rate, keyed to the fields surfaced in the detail/compare views.
export type RatedMetric =
  | "volatility" // annualized %, lower is better
  | "max_drawdown" // percent, closer to 0 is better (values are <= 0)
  | "sharpe" // ratio, higher is better
  | "sortino" // ratio, higher is better
  | "omega" // ratio, higher is better
  | "calmar" // ratio, higher is better
  | "martin"; // ratio, higher is better

// Threshold tables. Each entry decides good/ok given the value; anything not good/ok is
// bad. "higher is better" and "lower is better" variants read the two cutoffs differently.
const HIGHER_IS_BETTER: Partial<Record<RatedMetric, { good: number; ok: number }>> = {
  // Sharpe: >=1 is solid, >=0.5 is middling, below that is weak.
  sharpe: { good: 1, ok: 0.5 },
  // Sortino tends to run higher than Sharpe for the same series, so cutoffs are higher.
  sortino: { good: 1.5, ok: 0.75 },
  // Omega above 1 means gains above the hurdle outweigh losses below it.
  omega: { good: 1.2, ok: 1 },
  // Calmar: return per unit of max drawdown; >=1 is strong, >=0.5 is ok.
  calmar: { good: 1, ok: 0.5 },
  // Martin uses the typically smaller ulcer index, so its cutoffs are higher than Sharpe's.
  martin: { good: 2, ok: 1 },
};

const LOWER_IS_BETTER: Partial<Record<RatedMetric, { good: number; ok: number }>> = {
  // Annualized volatility: <=15% calm, <=25% moderate, above is high.
  volatility: { good: 15, ok: 25 },
};

/**
 * Rate a metric value. Returns null when the value is missing or the metric has no
 * meaningful quality direction.
 */
export function rateMetric(metric: RatedMetric, value: number | undefined | null): Quality | null {
  if (value === undefined || value === null || Number.isNaN(value)) return null;

  if (metric === "max_drawdown") {
    // Drawdowns are reported as <= 0; use magnitude. Shallow is good, deep is bad.
    const depth = Math.abs(value);
    if (depth <= 20) return "good";
    if (depth <= 40) return "ok";
    return "bad";
  }

  const higher = HIGHER_IS_BETTER[metric];
  if (higher) {
    if (value >= higher.good) return "good";
    if (value >= higher.ok) return "ok";
    return "bad";
  }

  const lower = LOWER_IS_BETTER[metric];
  if (lower) {
    if (value <= lower.good) return "good";
    if (value <= lower.ok) return "ok";
    return "bad";
  }

  return null;
}

/** Tailwind text-color class for a quality, or neutral when there is no rating. */
export function qualityColor(quality: Quality | null): string {
  switch (quality) {
    case "good":
      return "text-success";
    case "ok":
      return "text-warning";
    case "bad":
      return "text-destructive";
    default:
      return "";
  }
}

/** Short human label for a quality, used in tooltips/aria. */
export function qualityLabel(quality: Quality | null): string {
  switch (quality) {
    case "good":
      return "good";
    case "ok":
      return "ok";
    case "bad":
      return "bad";
    default:
      return "";
  }
}
